import { useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faSpinner } from '@fortawesome/free-solid-svg-icons';
import {
  apiErrorStatus,
  inspectionModificationService,
  MODIFICATION_TEXT_MAX,
  type ClosureOutcome,
  type ModificationClassification,
  type ModificationEntry,
  type ModificationRound,
  type QuotationRevision,
} from '@/services/inspection-modification.service';
import {
  ageDays,
  CLASSIFICATION_HINTS,
  CLASSIFICATION_LABELS,
  CLASSIFICATIONS,
  closureOutcomesFor,
  CLOSURE_OUTCOME_HINTS,
  COMMERCIAL_OUTCOME_LABELS,
  commercialViewOf,
  currentRevisionOf,
  currentRoundOf,
  ENTRY_TYPE_LABELS,
  nextActionFor,
  OPS_VERDICT_LABELS,
  ROUND_STATE_BADGE,
  ROUND_STATE_LABELS,
  SCOPE_IMPACT_LABELS,
  triageRequiresQuestion,
  WAITING_FOR_LABELS,
} from '@/features/inspection/modification-state-rules';
import { SkeletonCard } from '@/components/skeletons';
import { QueryErrorState } from '@/components/shared/QueryErrorState';
import { FormField } from '@/components/shared/FormField';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useHasPermission } from '@/hooks/use-permissions';
import { useAuthStore } from '@/stores/auth.store';
import { toast, swalConfirm } from '@/lib/swal';
import { getApiErrorMessage } from '@/lib/api-error';
import { isPdfFile } from '@/features/inspection/quotation-send-rules';

/**
 * D-131 M6 — the Commercial "Modification" panel on the job detail page
 * (design §8, §14.2 "a workbench, not a screen tour"; readiness §8.2).
 *
 * ── OWNERSHIP ───────────────────────────────────────────────────────────────
 * Commercial owns the customer conversation: it classifies (T2/T3/T4), asks
 * the customer (T12), refers to Operations (T13), shares the Operations note,
 * closes with an outcome (T8/T9/T10) or sends a revised quotation (T11). Every
 * customer-facing sentence on this panel is written by Commercial. Operations'
 * verdict is shown read-only; the customer is never an owner.
 *
 * ── WHAT IT DOES NOT DO ─────────────────────────────────────────────────────
 * No price is entered anywhere: a revision records the ERP quotation REFERENCE
 * (design §13.1 — a revision exists iff a new ERP quotation is issued). No
 * classification, verdict, outcome or message is invented client-side.
 *
 * ── AUTHORIZATION ───────────────────────────────────────────────────────────
 * `inspection:modification:manage` gates the conversation controls and
 * `inspection:commercial:send` the revision controls — UX courtesy only; the
 * server's PermissionGuard (403) and state machine (409) are the boundary and
 * both replies are handled in words below.
 */

export const MODIFICATION_ROUNDS_KEY = 'modification-rounds';
export const MODIFICATION_ENTRIES_KEY = 'modification-round-entries';
export const QUOTATION_REVISIONS_KEY = 'quotation-revisions';

function fmtDateTime(v: string | null | undefined): string {
  if (!v) return '—';
  return new Date(v).toLocaleString();
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="text-sm text-foreground">{children}</div>
    </div>
  );
}

function StateBadge({ round }: { round: ModificationRound }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${ROUND_STATE_BADGE[round.state]}`}
    >
      {ROUND_STATE_LABELS[round.state]}
    </span>
  );
}

/** One professional sentence per failure the workflow can honestly produce. */
function describeWriteFailure(error: unknown, fallback: string): string {
  switch (apiErrorStatus(error)) {
    case 403:
      return 'You do not have permission to manage modification requests on this job.';
    case 404:
      return 'This modification request is no longer available on this job.';
    case 409:
      return getApiErrorMessage(
        error,
        'This request has moved on since you opened it. The latest status is shown now.',
      );
    default:
      return getApiErrorMessage(error, fallback);
  }
}

function isStale(error: unknown): boolean {
  const s = apiErrorStatus(error);
  return s === 404 || s === 409;
}

interface ModificationPanelProps {
  inspectionId: string;
}

export function ModificationPanel({ inspectionId }: ModificationPanelProps) {
  const qc = useQueryClient();
  const can = useHasPermission();
  const myUserId = useAuthStore((s) => s.user?.userId ?? null);
  const canManage = can('inspection:modification:manage');
  const canSend = can('inspection:commercial:send');
  const canDownload = can('inspection:download-commercial-pdf');

  const roundsQuery = useQuery({
    queryKey: [MODIFICATION_ROUNDS_KEY, inspectionId],
    queryFn: () => inspectionModificationService.listRounds(inspectionId),
  });
  const revisionsQuery = useQuery({
    queryKey: [QUOTATION_REVISIONS_KEY, inspectionId],
    queryFn: () => inspectionModificationService.listRevisions(inspectionId),
  });

  const rounds = roundsQuery.data ?? [];
  const round = currentRoundOf(rounds);

  const entriesQuery = useQuery({
    queryKey: [MODIFICATION_ENTRIES_KEY, inspectionId, round?.id],
    queryFn: () => inspectionModificationService.listEntries(inspectionId, round!.id),
    enabled: !!round,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: [MODIFICATION_ROUNDS_KEY, inspectionId] });
    void qc.invalidateQueries({ queryKey: [MODIFICATION_ENTRIES_KEY, inspectionId] });
    void qc.invalidateQueries({ queryKey: [QUOTATION_REVISIONS_KEY, inspectionId] });
    void qc.invalidateQueries({ queryKey: ['inspection', inspectionId] });
    void qc.invalidateQueries({ queryKey: ['inspection-timeline', inspectionId] });
    void qc.invalidateQueries({ queryKey: ['dashboard-commercial'] });
  };

  if (roundsQuery.isLoading) {
    return (
      <div data-testid="modification-loading">
        <SkeletonCard count={1} minHeight={160} />
      </div>
    );
  }

  if (roundsQuery.isError) {
    if (apiErrorStatus(roundsQuery.error) === 403) {
      return (
        <div
          role="status"
          className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 px-4 py-3"
        >
          <FontAwesomeIcon icon={faLock} className="mt-0.5 h-4 w-4 text-muted-foreground" />
          <div>
            <p className="text-sm font-medium text-foreground">
              Modification requests are restricted
            </p>
            <p className="text-xs text-muted-foreground">
              Your role does not include access to this job&rsquo;s modification requests.
            </p>
          </div>
        </div>
      );
    }
    return (
      <QueryErrorState
        subject="the modification request"
        onRetry={() => void roundsQuery.refetch()}
      />
    );
  }

  if (!round) {
    return (
      <p className="text-sm text-muted-foreground">
        No modification request has been raised on this job.
      </p>
    );
  }

  const view = commercialViewOf(round);
  const revisions = revisionsQuery.data ?? [];
  const againstRevision = revisions.find((r) => r.id === round.againstRevisionId) ?? null;
  const currentRevision = currentRevisionOf(revisions);
  const earlierRounds = rounds
    .filter((r) => r.id !== round.id)
    .sort((a, b) => b.roundNumber - a.roundNumber);
  const isOpen = view !== 'CLOSED';
  const isMine = !!myUserId && round.assignedUserId === myUserId;

  return (
    <div className="space-y-5" data-testid="modification-panel" data-view={view}>
      {/* ── Header (design §14.2): round, state, owner, waiting for, age, assignee ── */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-foreground">
          Modification Request #{round.roundNumber}
        </span>
        <StateBadge round={round} />
        {isOpen && round.currentOwner && (
          <span className="text-xs text-muted-foreground">
            Owner: {round.currentOwner === 'COMMERCIAL' ? 'Commercial' : 'Operations'}
          </span>
        )}
        {isOpen && round.waitingFor && (
          <span className="text-xs text-muted-foreground">
            · Waiting on {WAITING_FOR_LABELS[round.waitingFor]}
          </span>
        )}
        <span className="text-xs text-muted-foreground">· {ageDays(round.createdAt)}d old</span>
        {isMine && isOpen && (
          <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground">
            You are handling this request
          </span>
        )}
      </div>

      {/* ── Next action ─────────────────────────────────────────────────── */}
      <p className="rounded-lg border border-primary/20 bg-primary/5 px-4 py-2 text-sm text-foreground">
        {nextActionFor(round)}
      </p>

      {/* ── Request ─────────────────────────────────────────────────────── */}
      <div className="rounded-lg border border-border bg-card px-4 py-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          What the customer asked for
        </p>
        <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{round.requestText}</p>
        <p className="mt-2 text-xs text-muted-foreground">
          Requested by the customer {fmtDateTime(round.createdAt)}
          {againstRevision && (
            <>
              {' '}
              · against quotation {againstRevision.quotationReference ??
                '(no reference)'} (revision {againstRevision.revisionNumber}, sent{' '}
              {fmtDateTime(againstRevision.sentAt)})
            </>
          )}
        </p>
      </div>

      {/* ── Triage ──────────────────────────────────────────────────────── */}
      {view === 'TRIAGE' ? (
        canManage ? (
          <TriageForm inspectionId={inspectionId} roundId={round.id} onDone={refresh} />
        ) : (
          <p role="status" className="text-sm text-muted-foreground">
            Waiting for a Commercial user with modification-management permission to classify this
            request.
          </p>
        )
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <Field label="Classification">
            {round.classification ? CLASSIFICATION_LABELS[round.classification] : '—'}
          </Field>
          {round.classifiedAt && (
            <Field label="Classified">{fmtDateTime(round.classifiedAt)}</Field>
          )}
        </div>
      )}

      {/* ── Operations ──────────────────────────────────────────────────── */}
      {round.opsVerdict && (
        <OperationsZone
          round={round}
          inspectionId={inspectionId}
          canManage={canManage && isOpen}
          canRefer={canManage && view === 'DECISION'}
          onDone={refresh}
        />
      )}
      {view === 'WITH_OPERATIONS' && (
        <p role="status" className="text-sm text-muted-foreground">
          Operations is assessing technical feasibility.
        </p>
      )}

      {/* ── Quotation ───────────────────────────────────────────────────── */}
      <QuotationZone
        inspectionId={inspectionId}
        revisions={revisions}
        current={currentRevision}
        canDownload={canDownload}
      />

      {/* ── Decision ────────────────────────────────────────────────────── */}
      {view === 'DECISION' &&
        (canManage || canSend ? (
          <DecisionZone
            inspectionId={inspectionId}
            round={round}
            canManage={canManage}
            canSend={canSend}
            onDone={refresh}
          />
        ) : (
          <p role="status" className="text-sm text-muted-foreground">
            Waiting for a Commercial user with modification-management permission to decide.
          </p>
        ))}

      {view === 'AWAITING_CUSTOMER_ANSWER' && (
        <p role="status" className="text-sm text-muted-foreground">
          A question is with the customer. Their reply will appear in the conversation below.
        </p>
      )}
      {view === 'AWAITING_CUSTOMER_DECISION' && (
        <p role="status" className="text-sm text-muted-foreground">
          The revised quotation is with the customer for approval or rejection.
        </p>
      )}

      {view === 'CLOSED' && (
        <div className="rounded-lg border border-border bg-muted/40 px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Outcome
          </p>
          <p className="mt-1 text-sm text-foreground">
            {round.commercialOutcome
              ? COMMERCIAL_OUTCOME_LABELS[round.commercialOutcome]
              : ROUND_STATE_LABELS[round.state]}
          </p>
          {round.closureMessage && (
            <p className="mt-1 whitespace-pre-wrap text-sm text-muted-foreground">
              {round.closureMessage}
            </p>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            Closed {fmtDateTime(round.closedAt)} · A further customer request opens a new round.
          </p>
        </div>
      )}

      {/* ── Thread & notes ──────────────────────────────────────────────── */}
      <Thread
        entries={entriesQuery.data ?? []}
        loading={entriesQuery.isLoading}
        failed={entriesQuery.isError}
        onRetry={() => void entriesQuery.refetch()}
      />

      {isOpen && canManage && (
        <InternalNoteForm inspectionId={inspectionId} roundId={round.id} onDone={refresh} />
      )}

      {earlierRounds.length > 0 && <EarlierRounds rounds={earlierRounds} />}
    </div>
  );
}

// ── Triage (T2 / T3 / T4) ───────────────────────────────────────────────────

function TriageForm({
  inspectionId,
  roundId,
  onDone,
}: {
  inspectionId: string;
  roundId: string;
  onDone: () => void;
}) {
  const [classification, setClassification] = useState<ModificationClassification | null>(null);
  const [question, setQuestion] = useState('');
  const [errors, setErrors] = useState<{ classification?: string; question?: string }>({});
  const submitting = useRef(false);

  const mutation = useMutation({
    mutationFn: (payload: { classification: ModificationClassification; question?: string }) =>
      inspectionModificationService.triage(inspectionId, roundId, payload),
    onSuccess: (_r, payload) => {
      toast.success(
        payload.classification === 'UNCLEAR'
          ? 'Question sent to the customer'
          : payload.classification === 'COMMERCIAL'
            ? 'Classified — ready for your decision'
            : 'Referred to Operations for a feasibility check',
      );
      onDone();
    },
    onError: (e: Error) => {
      toast.error(describeWriteFailure(e, 'Failed to classify the request'));
      if (isStale(e)) onDone();
    },
    onSettled: () => {
      submitting.current = false;
    },
  });
  const busy = mutation.isPending;
  const needsQuestion = classification ? triageRequiresQuestion(classification) : false;

  const submit = async () => {
    if (submitting.current || busy) return;
    const next: typeof errors = {};
    if (!classification) next.classification = 'Choose how to route this request.';
    if (needsQuestion && !question.trim()) next.question = 'Write the question for the customer.';
    setErrors(next);
    if (Object.keys(next).length > 0 || !classification) return;
    const ok = await swalConfirm({
      title:
        classification === 'UNCLEAR'
          ? 'Ask the customer to clarify?'
          : `Classify as ${CLASSIFICATION_LABELS[classification]}?`,
      text: CLASSIFICATION_HINTS[classification],
      confirmText: 'Confirm',
    });
    if (!ok || submitting.current || mutation.isPending) return;
    submitting.current = true;
    mutation.mutate({
      classification,
      ...(needsQuestion ? { question: question.trim() } : {}),
    });
  };

  return (
    <form
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      aria-labelledby="triage-heading"
      className="rounded-lg border border-sky-500/30 bg-sky-500/5 px-4 py-4"
    >
      <h3 id="triage-heading" className="text-sm font-semibold text-foreground">
        Classify the request
      </h3>
      <fieldset disabled={busy} className="mt-3 space-y-4">
        <div>
          <p id="classification-label" className="mb-2 text-sm font-medium text-foreground">
            Classification <span className="text-destructive">*</span>
          </p>
          <div
            role="radiogroup"
            aria-labelledby="classification-label"
            aria-required="true"
            className="grid grid-cols-1 gap-2 sm:grid-cols-2"
          >
            {CLASSIFICATIONS.map((c) => {
              const selected = classification === c;
              return (
                <button
                  key={c}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => {
                    setClassification(c);
                    setErrors({});
                  }}
                  className={`min-h-[44px] rounded-lg border px-3 py-2 text-left transition-colors ${
                    selected
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border bg-card text-foreground hover:bg-muted'
                  }`}
                >
                  <span className="block text-sm font-medium">{CLASSIFICATION_LABELS[c]}</span>
                  <span className="block text-xs text-muted-foreground">
                    {CLASSIFICATION_HINTS[c]}
                  </span>
                </button>
              );
            })}
          </div>
          {errors.classification && (
            <p role="alert" className="mt-1 text-xs text-destructive">
              {errors.classification}
            </p>
          )}
        </div>
        {needsQuestion && (
          <FormField
            label="Question for the customer"
            htmlFor="triage-question"
            required
            error={errors.question}
          >
            <Textarea
              id="triage-question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={3}
              maxLength={MODIFICATION_TEXT_MAX}
              aria-required="true"
              placeholder="What do you need the customer to explain?"
            />
          </FormField>
        )}
      </fieldset>
      <div className="mt-4 flex justify-end">
        <Button type="submit" disabled={busy} aria-disabled={busy}>
          {busy && <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />}
          {busy ? 'Saving…' : needsQuestion ? 'Ask the customer' : 'Classify'}
        </Button>
      </div>
    </form>
  );
}

// ── Operations zone (read-only verdict + publish + re-refer) ────────────────

function OperationsZone({
  round,
  inspectionId,
  canManage,
  canRefer,
  onDone,
}: {
  round: ModificationRound;
  inspectionId: string;
  canManage: boolean;
  canRefer: boolean;
  onDone: () => void;
}) {
  const [referNote, setReferNote] = useState('');
  const [referOpen, setReferOpen] = useState(false);

  const publish = useMutation({
    mutationFn: () => inspectionModificationService.publishOpsNote(inspectionId, round.id),
    onSuccess: () => {
      toast.success('Operations note shared with the customer');
      onDone();
    },
    onError: (e: Error) => {
      toast.error(describeWriteFailure(e, 'Failed to share the note'));
      if (isStale(e)) onDone();
    },
  });
  const refer = useMutation({
    mutationFn: (note: string) =>
      inspectionModificationService.referToOps(inspectionId, round.id, {
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: () => {
      toast.success('Referred back to Operations');
      setReferNote('');
      setReferOpen(false);
      onDone();
    },
    onError: (e: Error) => {
      toast.error(describeWriteFailure(e, 'Failed to refer to Operations'));
      if (isStale(e)) onDone();
    },
  });
  const busy = publish.isPending || refer.isPending;

  const handlePublish = async () => {
    if (busy) return;
    const ok = await swalConfirm({
      title: 'Share the Operations note with the customer?',
      text: 'The technical explanation below becomes visible to the customer, word for word, without naming Operations.',
      confirmText: 'Share',
    });
    if (ok && !publish.isPending) publish.mutate();
  };

  return (
    <section
      aria-label="Operations feasibility response"
      className="rounded-lg border border-border bg-card px-4 py-3"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Operations response
      </p>
      <p className="mt-1 text-sm font-medium text-foreground">
        {round.opsVerdict ? OPS_VERDICT_LABELS[round.opsVerdict] : '—'}
      </p>
      {round.opsTechnicalNote && (
        <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{round.opsTechnicalNote}</p>
      )}
      {round.opsAlternativeDescription && (
        <div className="mt-2">
          <Field label="Alternative">
            <span className="whitespace-pre-wrap">{round.opsAlternativeDescription}</span>
          </Field>
        </div>
      )}
      {(round.opsScopeImpact || round.opsAffectsParts || round.opsAffectsLabour) && (
        <p className="mt-2 text-xs text-muted-foreground">
          {[
            round.opsScopeImpact ? SCOPE_IMPACT_LABELS[round.opsScopeImpact] : null,
            round.opsAffectsParts ? 'Affects parts' : null,
            round.opsAffectsLabour ? 'Affects labour' : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      )}
      <p className="mt-2 text-xs text-muted-foreground">
        Responded {fmtDateTime(round.opsRespondedAt)} · Internal until shared
      </p>
      {(canManage || canRefer) && (
        <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:flex-wrap">
          {canManage && round.opsTechnicalNote && (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => void handlePublish()}
              disabled={busy}
            >
              {publish.isPending && (
                <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3 w-3 animate-spin" />
              )}
              Share technical note with the customer
            </Button>
          )}
          {canRefer && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setReferOpen((o) => !o)}
              disabled={busy}
              aria-expanded={referOpen}
            >
              Refer back to Operations
            </Button>
          )}
        </div>
      )}
      {canRefer && referOpen && (
        <form
          noValidate
          className="mt-3 space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (busy) return;
            void (async () => {
              const ok = await swalConfirm({
                title: 'Refer back to Operations?',
                text: 'The round returns to Operations for another feasibility pass.',
                confirmText: 'Refer',
              });
              if (ok && !refer.isPending) refer.mutate(referNote);
            })();
          }}
        >
          <FormField label="Note for Operations (optional, internal)" htmlFor="refer-note">
            <Textarea
              id="refer-note"
              value={referNote}
              onChange={(e) => setReferNote(e.target.value)}
              rows={2}
              maxLength={MODIFICATION_TEXT_MAX}
              disabled={busy}
              placeholder="What should Operations look at again?"
            />
          </FormField>
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={busy}>
              {refer.isPending && (
                <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3 w-3 animate-spin" />
              )}
              {refer.isPending ? 'Referring…' : 'Refer to Operations'}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}

// ── Quotation zone ──────────────────────────────────────────────────────────

const REVISION_STATUS_LABELS: Record<QuotationRevision['status'], string> = {
  DRAFT: 'Draft — not sent',
  SENT: 'Sent — awaiting customer decision',
  ACCEPTED: 'Accepted by the customer',
  REJECTED: 'Rejected by the customer',
  SUPERSEDED: 'Superseded',
};

/**
 * G-4 — opens the revision's ERP PDF through an AME-signed URL fetched on
 * demand (the URL is short-lived, so it is never cached with the list).
 * Superseded revisions are retained and remain downloadable (D-131 OQ-2).
 */
function RevisionDownload({
  inspectionId,
  revision,
  canDownload,
}: {
  inspectionId: string;
  revision: QuotationRevision;
  canDownload: boolean;
}) {
  const mutation = useMutation({
    mutationFn: () =>
      inspectionModificationService.getRevisionDocumentUrl(inspectionId, revision.id),
    onSuccess: (url) => {
      if (!url) {
        toast.error('This revision has no PDF to download.');
        return;
      }
      window.open(url, '_blank', 'noopener');
    },
    onError: (e: Error) => toast.error(describeWriteFailure(e, 'Failed to open the quotation PDF')),
  });
  if (!canDownload || !revision.documentAssetId) return null;
  return (
    <button
      type="button"
      className="ml-2 min-h-[44px] text-xs font-medium text-primary hover:underline sm:min-h-0"
      onClick={() => mutation.mutate()}
      disabled={mutation.isPending}
      aria-label={`Download PDF of revision ${revision.revisionNumber}`}
    >
      {mutation.isPending ? 'Opening…' : 'Download PDF'}
    </button>
  );
}

function QuotationZone({
  inspectionId,
  revisions,
  current,
  canDownload,
}: {
  inspectionId: string;
  revisions: QuotationRevision[];
  current: { revisionNumber: number; status: string } | null;
  canDownload: boolean;
}) {
  const [showAll, setShowAll] = useState(false);
  const currentRow = current
    ? (revisions.find((r) => r.revisionNumber === current.revisionNumber) ?? null)
    : null;
  const others = revisions
    .filter((r) => r.id !== currentRow?.id)
    .sort((a, b) => b.revisionNumber - a.revisionNumber);
  return (
    <section aria-label="Quotation revisions" className="rounded-lg border border-border px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Quotation
      </p>
      {currentRow ? (
        <p className="mt-1 text-sm text-foreground">
          Current: revision {currentRow.revisionNumber} ·{' '}
          {currentRow.quotationReference ?? 'no reference'} ·{' '}
          {REVISION_STATUS_LABELS[currentRow.status]}
          {currentRow.sentAt && (
            <span className="text-muted-foreground"> · sent {fmtDateTime(currentRow.sentAt)}</span>
          )}
          {currentRow.documentName && (
            <span className="text-muted-foreground"> · {currentRow.documentName}</span>
          )}
          <RevisionDownload
            inspectionId={inspectionId}
            revision={currentRow}
            canDownload={canDownload}
          />
        </p>
      ) : (
        <p className="mt-1 text-sm text-muted-foreground">No quotation revision has been sent.</p>
      )}
      {others.length > 0 && (
        <div className="mt-2">
          <button
            type="button"
            className="min-h-[44px] text-xs font-medium text-primary hover:underline sm:min-h-0"
            onClick={() => setShowAll((v) => !v)}
            aria-expanded={showAll}
          >
            {showAll ? 'Hide other revisions' : `Show other revisions (${others.length})`}
          </button>
          {showAll && (
            <ul className="mt-1 space-y-1">
              {others.map((r) => (
                <li key={r.id} className="text-xs text-muted-foreground">
                  Revision {r.revisionNumber} · {r.quotationReference ?? 'no reference'} ·{' '}
                  {REVISION_STATUS_LABELS[r.status]}
                  {r.sentAt ? ` · sent ${fmtDateTime(r.sentAt)}` : ''}
                  <RevisionDownload
                    inspectionId={inspectionId}
                    revision={r}
                    canDownload={canDownload}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}

// ── Decision zone (T8 / T9 / T10 / T11 / T12) ───────────────────────────────

type DecisionBranch = 'CLOSE' | 'SEND' | 'ASK';

function DecisionZone({
  inspectionId,
  round,
  canManage,
  canSend,
  onDone,
}: {
  inspectionId: string;
  round: ModificationRound;
  canManage: boolean;
  canSend: boolean;
  onDone: () => void;
}) {
  const [branch, setBranch] = useState<DecisionBranch | null>(null);
  const allowed = closureOutcomesFor(round);

  return (
    <section
      aria-labelledby="decision-heading"
      className="rounded-lg border border-purple-500/30 bg-purple-500/5 px-4 py-4"
    >
      <h3 id="decision-heading" className="text-sm font-semibold text-foreground">
        Does this need a new quotation?
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        A revision exists only if a new ERP quotation is issued. Otherwise, close with an answer —
        the customer keeps the current quotation to approve or reject.
      </p>
      <div
        role="radiogroup"
        aria-label="Decision"
        className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-3"
      >
        {canManage && (
          <button
            type="button"
            role="radio"
            aria-checked={branch === 'CLOSE'}
            onClick={() => setBranch('CLOSE')}
            className={`min-h-[44px] rounded-lg border px-3 py-2 text-left ${branch === 'CLOSE' ? 'border-primary bg-primary/10' : 'border-border bg-card hover:bg-muted'}`}
          >
            <span className="block text-sm font-medium text-foreground">Close with an answer</span>
            <span className="block text-xs text-muted-foreground">No new quotation.</span>
          </button>
        )}
        {canSend && (
          <button
            type="button"
            role="radio"
            aria-checked={branch === 'SEND'}
            onClick={() => setBranch('SEND')}
            className={`min-h-[44px] rounded-lg border px-3 py-2 text-left ${branch === 'SEND' ? 'border-primary bg-primary/10' : 'border-border bg-card hover:bg-muted'}`}
          >
            <span className="block text-sm font-medium text-foreground">
              Send a revised quotation
            </span>
            <span className="block text-xs text-muted-foreground">
              A new ERP quotation was issued.
            </span>
          </button>
        )}
        {canManage && (
          <button
            type="button"
            role="radio"
            aria-checked={branch === 'ASK'}
            onClick={() => setBranch('ASK')}
            className={`min-h-[44px] rounded-lg border px-3 py-2 text-left ${branch === 'ASK' ? 'border-primary bg-primary/10' : 'border-border bg-card hover:bg-muted'}`}
          >
            <span className="block text-sm font-medium text-foreground">Ask the customer</span>
            <span className="block text-xs text-muted-foreground">
              You need something from them first.
            </span>
          </button>
        )}
      </div>

      {branch === 'CLOSE' && (
        <CloseForm
          inspectionId={inspectionId}
          roundId={round.id}
          allowed={allowed}
          onDone={onDone}
        />
      )}
      {branch === 'SEND' && (
        <SendRevisionForm inspectionId={inspectionId} roundId={round.id} onDone={onDone} />
      )}
      {branch === 'ASK' && (
        <ClarifyForm inspectionId={inspectionId} roundId={round.id} onDone={onDone} />
      )}
    </section>
  );
}

function CloseForm({
  inspectionId,
  roundId,
  allowed,
  onDone,
}: {
  inspectionId: string;
  roundId: string;
  allowed: readonly ClosureOutcome[];
  onDone: () => void;
}) {
  const [outcome, setOutcome] = useState<ClosureOutcome | null>(null);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState<{ outcome?: string; message?: string }>({});
  const submitting = useRef(false);

  const mutation = useMutation({
    mutationFn: (payload: { outcome: ClosureOutcome; closureMessage: string }) =>
      inspectionModificationService.close(inspectionId, roundId, payload),
    onSuccess: () => {
      toast.success('Request closed and the customer informed');
      onDone();
    },
    onError: (e: Error) => {
      toast.error(describeWriteFailure(e, 'Failed to close the request'));
      if (isStale(e)) onDone();
    },
    onSettled: () => {
      submitting.current = false;
    },
  });
  const busy = mutation.isPending;

  const submit = async () => {
    if (submitting.current || busy) return;
    const next: typeof errors = {};
    if (!outcome) next.outcome = 'Choose the outcome.';
    if (!message.trim()) next.message = 'Write the message the customer will read.';
    setErrors(next);
    if (Object.keys(next).length > 0 || !outcome) return;
    const ok = await swalConfirm({
      title: `Close as "${COMMERCIAL_OUTCOME_LABELS[outcome]}"?`,
      text: 'The customer is told this outcome and your message. The current quotation stays available to them.',
      confirmText: 'Close request',
    });
    if (!ok || submitting.current || mutation.isPending) return;
    submitting.current = true;
    mutation.mutate({ outcome, closureMessage: message.trim() });
  };

  return (
    <form
      noValidate
      className="mt-4 space-y-4"
      aria-label="Close with an answer"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <fieldset disabled={busy} className="space-y-4">
        <div>
          <p id="outcome-label" className="mb-2 text-sm font-medium text-foreground">
            Outcome <span className="text-destructive">*</span>
          </p>
          <div
            role="radiogroup"
            aria-labelledby="outcome-label"
            aria-required="true"
            className="grid grid-cols-1 gap-2"
          >
            {(['ACCEPTED_NO_CHANGE', 'DECLINED', 'NOT_FEASIBLE'] as const).map((o) => {
              const enabled = allowed.includes(o);
              const selected = outcome === o;
              return (
                <button
                  key={o}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-disabled={!enabled}
                  disabled={!enabled}
                  onClick={() => {
                    setOutcome(o);
                    setErrors((er) => ({ ...er, outcome: undefined }));
                  }}
                  className={`min-h-[44px] rounded-lg border px-3 py-2 text-left disabled:cursor-not-allowed disabled:opacity-50 ${
                    selected
                      ? 'border-primary bg-primary/10 text-foreground'
                      : 'border-border bg-card text-foreground hover:bg-muted'
                  }`}
                >
                  <span className="block text-sm font-medium">{COMMERCIAL_OUTCOME_LABELS[o]}</span>
                  <span className="block text-xs text-muted-foreground">
                    {CLOSURE_OUTCOME_HINTS[o]}
                  </span>
                </button>
              );
            })}
          </div>
          {errors.outcome && (
            <p role="alert" className="mt-1 text-xs text-destructive">
              {errors.outcome}
            </p>
          )}
        </div>
        <FormField
          label="Message to the customer"
          htmlFor="close-message"
          required
          error={errors.message}
        >
          <Textarea
            id="close-message"
            value={message}
            onChange={(e) => {
              setMessage(e.target.value);
              setErrors((er) => ({ ...er, message: undefined }));
            }}
            rows={4}
            maxLength={MODIFICATION_TEXT_MAX}
            aria-required="true"
            placeholder="What the customer will read about this outcome…"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Customer-visible. Written by Commercial; never mention internal discussion.
          </p>
        </FormField>
      </fieldset>
      <div className="flex justify-end">
        <Button type="submit" disabled={busy} aria-disabled={busy}>
          {busy && <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />}
          {busy ? 'Closing…' : 'Close request'}
        </Button>
      </div>
    </form>
  );
}

function SendRevisionForm({
  inspectionId,
  roundId,
  onDone,
}: {
  inspectionId: string;
  roundId: string;
  onDone: () => void;
}) {
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<{ reference?: string; file?: string }>({});
  const submitting = useRef(false);

  // G-4 — the lifecycle in one action, in order: draft (reference / reason /
  // origin round) → attach the ERP-produced PDF TO THAT DRAFT → send (T11).
  // The server refuses a send without a PDF, so the form requires one up front.
  const mutation = useMutation({
    mutationFn: async (payload: {
      quotationReference: string;
      revisionReason: string;
      file: File;
    }) => {
      const draft = await inspectionModificationService.upsertDraftRevision(inspectionId, {
        quotationReference: payload.quotationReference,
        ...(payload.revisionReason ? { revisionReason: payload.revisionReason } : {}),
        roundId,
      });
      await inspectionModificationService.attachRevisionDocument(
        inspectionId,
        draft.id,
        payload.file,
      );
      return inspectionModificationService.sendRevision(inspectionId, draft.id, { roundId });
    },
    onSuccess: ({ revision }) => {
      toast.success(`Revised quotation (revision ${revision.revisionNumber}) sent to the customer`);
      onDone();
    },
    onError: (e: Error) => {
      toast.error(describeWriteFailure(e, 'Failed to send the revised quotation'));
      if (isStale(e)) onDone();
    },
    onSettled: () => {
      submitting.current = false;
    },
  });
  const busy = mutation.isPending;

  const pickFile = (picked: File | null) => {
    if (!picked) return;
    if (!isPdfFile(picked)) {
      setFile(null);
      setErrors((er) => ({ ...er, file: 'Only PDF files are accepted for the quotation.' }));
      return;
    }
    setFile(picked);
    setErrors((er) => ({ ...er, file: undefined }));
  };

  const submit = async () => {
    if (submitting.current || busy) return;
    const next: typeof errors = {};
    if (!reference.trim())
      next.reference = 'Enter the ERP quotation reference for the revised quotation.';
    if (!file) next.file = 'Attach the quotation PDF issued by the ERP.';
    setErrors(next);
    if (Object.keys(next).length > 0 || !file) return;
    const ok = await swalConfirm({
      title: 'Send the revised quotation?',
      text: `Quotation ${reference.trim()} (${file.name}) becomes the current quotation for the customer to approve or reject. The previous one is kept as superseded.`,
      confirmText: 'Send',
    });
    if (!ok || submitting.current || mutation.isPending) return;
    submitting.current = true;
    mutation.mutate({ quotationReference: reference.trim(), revisionReason: reason.trim(), file });
  };

  return (
    <form
      noValidate
      className="mt-4 space-y-4"
      aria-label="Send a revised quotation"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <fieldset disabled={busy} className="space-y-4">
        <FormField
          label="ERP quotation reference"
          htmlFor="revision-reference"
          required
          error={errors.reference}
        >
          <Input
            id="revision-reference"
            value={reference}
            onChange={(e) => {
              setReference(e.target.value);
              setErrors((er) => ({ ...er, reference: undefined }));
            }}
            maxLength={255}
            aria-required="true"
            placeholder="e.g. QT-2026-INS-00004"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Only the reference of the quotation issued in the ERP is recorded here.
          </p>
        </FormField>
        <FormField
          label="Quotation document (PDF)"
          htmlFor="revision-document"
          required
          error={errors.file}
        >
          <input
            id="revision-document"
            type="file"
            accept="application/pdf,.pdf"
            aria-required="true"
            className="block w-full text-sm text-foreground file:mr-3 file:rounded-lg file:border file:border-input file:bg-card file:px-3 file:py-2 file:text-sm"
            onChange={(e) => {
              pickFile(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
          {file && (
            <p className="mt-1 text-xs text-foreground" data-testid="revision-doc-picked">
              {file.name}
            </p>
          )}
          <p className="mt-1 text-xs text-muted-foreground">
            The PDF produced by the ERP for this quotation. The customer approves against it.
          </p>
        </FormField>
        <FormField label="What changed (optional, internal)" htmlFor="revision-reason">
          <Textarea
            id="revision-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            maxLength={MODIFICATION_TEXT_MAX}
            placeholder="Why this revision was issued…"
          />
        </FormField>
      </fieldset>
      <div className="flex justify-end">
        <Button type="submit" disabled={busy} aria-disabled={busy}>
          {busy && <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />}
          {busy ? 'Sending…' : 'Send revised quotation'}
        </Button>
      </div>
    </form>
  );
}

function ClarifyForm({
  inspectionId,
  roundId,
  onDone,
}: {
  inspectionId: string;
  roundId: string;
  onDone: () => void;
}) {
  const [question, setQuestion] = useState('');
  const [error, setError] = useState<string | undefined>();
  const submitting = useRef(false);
  const mutation = useMutation({
    mutationFn: (q: string) =>
      inspectionModificationService.clarify(inspectionId, roundId, { question: q }),
    onSuccess: () => {
      toast.success('Question sent to the customer');
      onDone();
    },
    onError: (e: Error) => {
      toast.error(describeWriteFailure(e, 'Failed to send the question'));
      if (isStale(e)) onDone();
    },
    onSettled: () => {
      submitting.current = false;
    },
  });
  const busy = mutation.isPending;

  const submit = async () => {
    if (submitting.current || busy) return;
    if (!question.trim()) {
      setError('Write the question for the customer.');
      return;
    }
    setError(undefined);
    const ok = await swalConfirm({
      title: 'Ask the customer?',
      text: 'The customer receives this question and the request waits for their reply.',
      confirmText: 'Send question',
    });
    if (!ok || submitting.current || mutation.isPending) return;
    submitting.current = true;
    mutation.mutate(question.trim());
  };

  return (
    <form
      noValidate
      className="mt-4 space-y-4"
      aria-label="Ask the customer"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <FormField
        label="Question for the customer"
        htmlFor="clarify-question"
        required
        error={error}
      >
        <Textarea
          id="clarify-question"
          value={question}
          onChange={(e) => {
            setQuestion(e.target.value);
            setError(undefined);
          }}
          rows={3}
          maxLength={MODIFICATION_TEXT_MAX}
          aria-required="true"
          disabled={busy}
          placeholder="What do you need to know before deciding?"
        />
      </FormField>
      <div className="flex justify-end">
        <Button type="submit" disabled={busy} aria-disabled={busy}>
          {busy && <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3.5 w-3.5 animate-spin" />}
          {busy ? 'Sending…' : 'Send question'}
        </Button>
      </div>
    </form>
  );
}

// ── Thread, notes, history ──────────────────────────────────────────────────

function Thread({
  entries,
  loading,
  failed,
  onRetry,
}: {
  entries: ModificationEntry[];
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
}) {
  const [open, setOpen] = useState(true);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="min-h-[44px] text-sm font-medium text-primary hover:underline"
      >
        {open ? 'Hide conversation' : `Show conversation (${entries.length})`}
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {loading && <p className="text-sm text-muted-foreground">Loading conversation…</p>}
          {failed && (
            <p className="text-sm text-muted-foreground">
              The conversation could not be loaded.{' '}
              <button type="button" className="text-primary hover:underline" onClick={onRetry}>
                Retry
              </button>
            </p>
          )}
          {!loading && !failed && entries.length === 0 && (
            <p className="text-sm text-muted-foreground">No entries yet.</p>
          )}
          {entries.map((e) => (
            <div
              key={e.id}
              className={`rounded-lg border px-3 py-2 ${
                e.visibility === 'INTERNAL'
                  ? 'border-dashed border-border bg-muted/30'
                  : 'border-border'
              }`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-medium text-foreground">
                  {ENTRY_TYPE_LABELS[e.entryType] ?? e.entryType}
                </span>
                <span className="text-xs text-muted-foreground">
                  {e.authorKind === 'CUSTOMER'
                    ? 'Customer'
                    : e.authorKind === 'STAFF'
                      ? 'Staff'
                      : 'System'}
                </span>
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] ${
                    e.visibility === 'INTERNAL'
                      ? 'bg-muted text-muted-foreground'
                      : 'bg-green-500/10 text-green-700'
                  }`}
                >
                  {e.visibility === 'INTERNAL' ? 'Internal' : 'Customer-visible'}
                </span>
                <span className="ml-auto text-xs text-muted-foreground">
                  {fmtDateTime(e.createdAt)}
                </span>
              </div>
              {e.body && (
                <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{e.body}</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InternalNoteForm({
  inspectionId,
  roundId,
  onDone,
}: {
  inspectionId: string;
  roundId: string;
  onDone: () => void;
}) {
  const [body, setBody] = useState('');
  const mutation = useMutation({
    mutationFn: (text: string) =>
      inspectionModificationService.addCommercialNote(inspectionId, roundId, text),
    onSuccess: () => {
      toast.success('Internal note added');
      setBody('');
      onDone();
    },
    onError: (e: Error) => {
      toast.error(describeWriteFailure(e, 'Failed to add the note'));
      if (isStale(e)) onDone();
    },
  });
  const busy = mutation.isPending;
  return (
    <form
      className="space-y-2"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (busy || !body.trim()) return;
        mutation.mutate(body.trim());
      }}
    >
      <FormField
        label="Internal note (never shown to the customer)"
        htmlFor="commercial-internal-note"
      >
        <Textarea
          id="commercial-internal-note"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={2}
          maxLength={MODIFICATION_TEXT_MAX}
          disabled={busy}
          placeholder="Context for your team…"
        />
      </FormField>
      <div className="flex justify-end">
        <Button type="submit" variant="secondary" size="sm" disabled={busy || !body.trim()}>
          {busy && <FontAwesomeIcon icon={faSpinner} className="mr-2 h-3 w-3 animate-spin" />}
          {busy ? 'Adding…' : 'Add internal note'}
        </Button>
      </div>
    </form>
  );
}

function EarlierRounds({ rounds }: { rounds: ModificationRound[] }) {
  return (
    <div>
      <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Earlier requests on this job
      </p>
      <ul className="space-y-1">
        {rounds.map((r) => (
          <li key={r.id} className="flex flex-wrap items-center gap-2 text-sm text-foreground">
            <span>Request #{r.roundNumber}</span>
            <StateBadge round={r} />
            {r.commercialOutcome && (
              <span className="text-xs text-muted-foreground">
                {COMMERCIAL_OUTCOME_LABELS[r.commercialOutcome]}
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {fmtDateTime(r.closedAt ?? r.updatedAt)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
